import PropTypes from 'prop-types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { SearchOrAddData } from '@components/common'
import { X } from 'lucide-react'

import { ContactForm, ContactInfo } from '@components/client'
import { AdasBox, AdasButton, AdasDrawer, AdasTable, AdasTypography } from '@components/wrapper-components'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { CaradasSnackbar } from '@components/snackbar'
import { updateContact, getClientContacts } from '../../../api/api'
import { GENERIC_MESSAGES, CLIENT_CONTACT_LIST_HEADERS } from '../../../constants'
import { Logger } from '../../../logger'

export const ClientContactList = (props) => {
  const { clientId } = props
  const [openSnackBar, setOpenSnackBar] = useState(false)
  const [originalContacts, setOriginalContacts] = useState([])
  const [filteredContacts, setFilteredContacts] = useState([])
  const [sortedContacts, setSortedContacts] = useState([])
  const [isAddContact, setIsAddContact] = useState(false)
  const [isViewContact, setIsViewContact] = useState(false)
  const [selectedContact, setSelectedContact] = useState({})
  const deletedContactRef = useRef()
  const setLoading = useLoadingStore((store) => store.setLoading)
  const [searchInput, setSearchInput] = useState('')
  const [sort, setSort] = useState('ASC')

  const searchInputHandler = (e) => {
    setSearchInput(e.target.value)
  }

  const onSaveContactSuccessHandler = () => {
    if (isAddContact) {
      setIsAddContact(false)
    }
    fetchClientContactDetails()
  }

  const onDeleteContactHandler = (props) => {
    deletedContactRef.current = props
    fetchClientContactDetails()
    setOpenSnackBar(true)
    setIsViewContact(false)
  }

  const undoDeleteContactHandler = async () => {
    try {
      const updateContactResponse = await updateContact({
        data: deletedContactRef.current.data
      })

      if (updateContactResponse.status === 200) {
        fetchClientContactDetails()
      }
      setOpenSnackBar(false)
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'ClientInfo', method: 'undoDeleteContactHandler' } })
    }
  }

  const fetchClientContactDetails = useCallback(async () => {
    setLoading(true)
    try {
      const clientContactsResponse = await getClientContacts({ clientId })
      if (clientContactsResponse?.status === 200) {
        setOriginalContacts(clientContactsResponse.data)
        setFilteredContacts(clientContactsResponse.data)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { clientId } })
    } finally {
      setLoading(false)
    }
  }, [clientId])

  useEffect(() => {
    const performSearch = () => {
      const filtered = originalContacts.filter((contact) =>
        Object.values(contact).some((value) => String(value).toLowerCase().includes(searchInput.toLowerCase()))
      )
      setFilteredContacts(filtered)
    }

    const timeoutId = setTimeout(performSearch, 1000)
    return () => clearTimeout(timeoutId)
  }, [searchInput, originalContacts])

  useEffect(() => {
    const sorted = [...filteredContacts].sort((a, b) => {
      if (sort === 'ASC') return a.name?.localeCompare(b.name)
      return b.name?.localeCompare(a.name)
    })
    setSortedContacts(sorted)
  }, [filteredContacts, sort])

  const clickSortHandler = useCallback((id) => {
    if (id === 'name') {
      setSort((prevSort) => (prevSort === 'ASC' ? 'DESC' : 'ASC'))
    }
  }, [])

  useEffect(() => {
    fetchClientContactDetails()
  }, [fetchClientContactDetails])

  const contactInfoHandler = (props) => {
    setIsViewContact(true)
    setSelectedContact(props)
  }

  return (
    <AdasBox>
      <AdasBox className='mt-5'>
        <SearchOrAddData searchInput={searchInput} searchInputHandler={searchInputHandler} onAddClick={() => setIsAddContact(true)} />
        {sortedContacts.length > 0 && (
          <AdasBox className='mt-5 overflow-hidden rounded-card border border-border'>
            <AdasTable
              containerProps={{ className: 'rounded-none border-none shadow-none' }}
              tableProps={{ 'aria-label': 'contacts' }}
              columns={CLIENT_CONTACT_LIST_HEADERS}
              gradientHeader
              sortField='name'
              sortDirection={sort}
              onSort={clickSortHandler}
              data={sortedContacts}
              onSelect={contactInfoHandler}
            />
          </AdasBox>
        )}
      </AdasBox>
      <AdasDrawer anchor={'right'} open={isViewContact} onClose={() => setIsViewContact(false)} className='bg-card'>
        <AdasBox className='drawer-title-container'>
          <AdasTypography className='text-[24px] font-bold text-foreground'>Contact</AdasTypography>
          <AdasButton buttonType='iconButton' className='text-info cursor-pointer p-0.5' onClick={() => setIsViewContact(false)}>
            <X className='w-4 h-4' />
          </AdasButton>
        </AdasBox>
        <ContactInfo contact={selectedContact} onSaveSuccess={onSaveContactSuccessHandler} deleteContactHandler={onDeleteContactHandler} />
      </AdasDrawer>
      <AdasDrawer anchor={'right'} open={isAddContact} onClose={() => setIsAddContact(false)} className='bg-card'>
        <AdasBox className='drawer-title-container'>
          <AdasTypography className='text-[24px] font-bold text-foreground'>Contact</AdasTypography>
          <AdasButton buttonType='iconButton' className='text-info cursor-pointer p-0.5' onClick={() => setIsAddContact(false)}>
            <X className='w-4 h-4' />
          </AdasButton>
        </AdasBox>
        <ContactForm
          isAddContact={isAddContact}
          deleteContactHandler={onDeleteContactHandler}
          contactData={{ client_id: clientId }}
          cancel={() => setIsAddContact(false)}
          onSaveSuccess={onSaveContactSuccessHandler}
        />
      </AdasDrawer>
      <AdasBox>
        <CaradasSnackbar
          open={openSnackBar}
          message={GENERIC_MESSAGES.CONTACT_DELETED}
          onClose={setOpenSnackBar}
          onUndo={undoDeleteContactHandler}
        />
      </AdasBox>
    </AdasBox>
  )
}

ClientContactList.propTypes = {
  clientId: PropTypes.string
}
